/**
 * Step 2: Compliance Information
 * Collects regulatory registrations and compliance officer details
 */

import React from 'react';
import { ShieldCheck, Plus, Trash, Upload, Check, X } from '@phosphor-icons/react';
import { 
  TextField, SelectField, ToggleField, MultiSelectChips, 
  StepSection, CollapsibleSection, DynamicList, FileUpload, InfoCard 
} from './SharedComponents';

const SA_REGULATORS = [
  { id: 'fsca', name: 'Financial Sector Conduct Authority', abbreviation: 'FSCA' },
  { id: 'pa', name: 'Prudential Authority', abbreviation: 'PA' },
  { id: 'info_reg', name: 'Information Regulator', abbreviation: 'IR' },
  { id: 'cipc', name: 'Companies and Intellectual Property Commission', abbreviation: 'CIPC' },
  { id: 'jse', name: 'Johannesburg Stock Exchange', abbreviation: 'JSE' },
  { id: 'sarb', name: 'South African Reserve Bank', abbreviation: 'SARB' },
  { id: 'sars', name: 'South African Revenue Service (FATCA/CRS)', abbreviation: 'SARS' },
  { id: 'ncr', name: 'National Credit Regulator', abbreviation: 'NCR' },
  { id: 'cms', name: 'Council for Medical Schemes', abbreviation: 'CMS' },
  { id: 'fic', name: 'Financial Intelligence Centre', abbreviation: 'FIC' },
];

const FSP_LICENSE_CATEGORIES = [
  { id: 'cat_i', name: 'Category I', description: 'Advice and intermediary services' },
  { id: 'cat_ii', name: 'Category II', description: 'Discretionary FSP' },
  { id: 'cat_iia', name: 'Category IIA', description: 'Hedge Fund FSP' },
  { id: 'cat_iii', name: 'Category III', description: 'Administrative FSP' },
  { id: 'cat_iv', name: 'Category IV', description: 'Assistance Business FSP' },
];

const FIC_SCHEDULE_ITEMS = [
  { id: 'schedule_1', name: 'Schedule 1 - Accountable Institutions' },
  { id: 'schedule_2', name: 'Schedule 2 - Supervisory Bodies' },
  { id: 'schedule_3', name: 'Schedule 3 - Reporting Institutions' },
];

const emptyOfficer = () => ({
  id: crypto.randomUUID(),
  name: '',
  surname: '',
  email: '',
  registration_date: '',
});

const Step2ComplianceInformation = ({ formData, updateField }) => {
  
  const addOfficer = (field) => {
    const current = formData[field] || [];
    updateField(field, [...current, emptyOfficer()]);
  };
  
  const updateOfficer = (field, index, key, value) => {
    const current = [...(formData[field] || [])];
    current[index] = { ...current[index], [key]: value };
    updateField(field, current);
  };
  
  const removeOfficer = (field, index) => {
    const current = [...(formData[field] || [])];
    current.splice(index, 1);
    updateField(field, current);
  };

  return (
    <StepSection
      title="Compliance Information"
      description="Enter your regulatory registrations and compliance officer details"
      icon={ShieldCheck}
    >
      {/* Compliance Function Type */}
      <div className="space-y-4">
        <label className="text-sm font-medium text-text-secondary">Compliance Function</label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <ToggleField
            label="Internal"
            checked={formData.compliance_function_internal || false}
            onChange={(v) => updateField('compliance_function_internal', v)}
            testId="compliance-internal-toggle"
          />
          <ToggleField
            label="External"
            checked={formData.compliance_function_external || false}
            onChange={(v) => updateField('compliance_function_external', v)}
            testId="compliance-external-toggle"
          />
          <ToggleField
            label="Co-sourced"
            checked={formData.compliance_function_cosourced || false}
            onChange={(v) => updateField('compliance_function_cosourced', v)}
            testId="compliance-cosourced-toggle"
          />
        </div>
        
        {(formData.compliance_function_external || formData.compliance_function_cosourced) && (
          <div className="p-4 bg-background-surface rounded-lg border border-border space-y-3">
            <TextField
              label="External Compliance Entity Name"
              value={formData.external_compliance_entity_name}
              onChange={(v) => updateField('external_compliance_entity_name', v)}
              placeholder="e.g., ABC Compliance Services (Pty) Ltd"
              testId="external-entity-name"
            />
            <TextField
              label="Registration Number"
              value={formData.external_compliance_entity_reg}
              onChange={(v) => updateField('external_compliance_entity_reg', v)}
              placeholder="Entity registration number"
              testId="external-entity-reg"
            />
            <FileUpload
              label="Service Level Agreement (SLA)"
              onUpload={(file) => updateField('external_compliance_sla', file)}
              uploadedFile={formData.external_compliance_sla}
              accept=".pdf,.doc,.docx"
              helpText="Upload your SLA with the external compliance provider"
              testId="external-sla-upload"
            />
          </div>
        )}
      </div>
      
      {/* Primary Regulators */}
      <MultiSelectChips
        label="Primary Regulators"
        options={SA_REGULATORS}
        selected={formData.primary_regulators || []}
        onChange={(v) => updateField('primary_regulators', v)}
        testId="primary-regulators"
      />
      
      {/* FSP Details */}
      <CollapsibleSection title="FSP License Details" testId="fsp-section">
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextField
              label="FSP Number"
              value={formData.fsp_number}
              onChange={(v) => updateField('fsp_number', v)}
              placeholder="e.g., 12345"
              testId="fsp-number"
            />
            <TextField
              label="Registration Date"
              type="date"
              value={formData.fsp_registration_date}
              onChange={(v) => updateField('fsp_registration_date', v)}
              testId="fsp-reg-date"
            />
          </div>
          
          <MultiSelectChips
            label="License Categories"
            options={FSP_LICENSE_CATEGORIES}
            selected={formData.fsp_categories || []}
            onChange={(v) => updateField('fsp_categories', v)}
            testId="fsp-categories"
          />
          
          <FileUpload
            label="FSP License Document"
            onUpload={(file) => updateField('fsp_license_document', file)}
            uploadedFile={formData.fsp_license_document}
            accept=".pdf,.png,.jpg"
            helpText="Upload your FSP license or FSCA screenshot"
            testId="fsp-license-upload"
          />
        </div>
      </CollapsibleSection>
      
      {/* FIC Details */}
      <CollapsibleSection title="FIC Registration" testId="fic-section">
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextField
              label="FIC Organization ID"
              value={formData.fic_org_id}
              onChange={(v) => updateField('fic_org_id', v)}
              placeholder="FIC registration ID"
              testId="fic-org-id"
            />
            <TextField
              label="Registration Date"
              type="date"
              value={formData.fic_registration_date}
              onChange={(v) => updateField('fic_registration_date', v)}
              testId="fic-reg-date"
            />
          </div>
          
          <MultiSelectChips
            label="FIC Schedule Items"
            options={FIC_SCHEDULE_ITEMS}
            selected={formData.fic_schedules || []}
            onChange={(v) => updateField('fic_schedules', v)}
            testId="fic-schedules"
          />
        </div>
      </CollapsibleSection>
      
      {/* Compliance Officers */}
      <CollapsibleSection title="FAIS Compliance Officers" testId="fais-officers-section">
        <DynamicList
          items={formData.fais_compliance_officers || []}
          onAdd={() => addOfficer('fais_compliance_officers')}
          onRemove={(idx) => removeOfficer('fais_compliance_officers', idx)}
          addButtonText="Add FAIS Compliance Officer"
          testId="fais-officers-list"
          renderItem={(officer, idx) => (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <TextField
                label="Name"
                value={officer.name}
                onChange={(v) => updateOfficer('fais_compliance_officers', idx, 'name', v)}
                placeholder="First name"
              />
              <TextField
                label="Surname"
                value={officer.surname}
                onChange={(v) => updateOfficer('fais_compliance_officers', idx, 'surname', v)}
                placeholder="Surname"
              />
              <TextField
                label="Email"
                type="email"
                value={officer.email}
                onChange={(v) => updateOfficer('fais_compliance_officers', idx, 'email', v)}
                placeholder="email@company.com"
              />
              <TextField
                label="Registration Date"
                type="date"
                value={officer.registration_date}
                onChange={(v) => updateOfficer('fais_compliance_officers', idx, 'registration_date', v)}
              />
            </div>
          )}
        />
      </CollapsibleSection>
      
      {/* Key Individuals */}
      <CollapsibleSection title="Key Individuals" testId="key-individuals-section">
        <DynamicList
          items={formData.key_individuals || []}
          onAdd={() => addOfficer('key_individuals')}
          onRemove={(idx) => removeOfficer('key_individuals', idx)}
          addButtonText="Add Key Individual"
          testId="key-individuals-list"
          renderItem={(ki, idx) => (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <TextField
                label="Name"
                value={ki.name}
                onChange={(v) => updateOfficer('key_individuals', idx, 'name', v)}
                placeholder="First name"
              />
              <TextField
                label="Surname"
                value={ki.surname}
                onChange={(v) => updateOfficer('key_individuals', idx, 'surname', v)}
                placeholder="Surname"
              />
              <TextField
                label="Email"
                type="email"
                value={ki.email}
                onChange={(v) => updateOfficer('key_individuals', idx, 'email', v)}
                placeholder="email@company.com"
              />
              <TextField
                label="Registration Date"
                type="date"
                value={ki.registration_date}
                onChange={(v) => updateOfficer('key_individuals', idx, 'registration_date', v)}
              />
            </div>
          )}
        />
      </CollapsibleSection>
      
      {/* MLROs */}
      <CollapsibleSection title="Money Laundering Reporting Officers (MLROs)" testId="mlro-section">
        <DynamicList
          items={formData.mlros || []}
          onAdd={() => addOfficer('mlros')}
          onRemove={(idx) => removeOfficer('mlros', idx)}
          addButtonText="Add MLRO"
          testId="mlro-list"
          renderItem={(mlro, idx) => (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <TextField
                label="Name"
                value={mlro.name}
                onChange={(v) => updateOfficer('mlros', idx, 'name', v)}
                placeholder="First name"
              />
              <TextField
                label="Surname"
                value={mlro.surname}
                onChange={(v) => updateOfficer('mlros', idx, 'surname', v)}
                placeholder="Surname"
              />
              <TextField
                label="Email"
                type="email"
                value={mlro.email}
                onChange={(v) => updateOfficer('mlros', idx, 'email', v)}
                placeholder="email@company.com"
              />
              <TextField
                label="Appointment Date"
                type="date"
                value={mlro.registration_date}
                onChange={(v) => updateOfficer('mlros', idx, 'registration_date', v)}
              />
            </div>
          )}
        />
      </CollapsibleSection>
      
      {/* Info Note */}
      <InfoCard variant="info" title="Important">
        This section must be maintained regularly. Legislation may require new appointments or re-appointments 
        to be approved by a Regulator within specified timeframes.
      </InfoCard>
    </StepSection>
  );
};

export default Step2ComplianceInformation;
